/**
 * Dropbox.
 *
 * The best fit of the one-click providers: PKCE is a first-class flow, and
 * refresh tokens do not expire unless revoked. That last property is why it
 * leads — a credential that dies of inactivity would make an unattended switch
 * fail silently, years later, with nobody able to fix it.
 *
 * `token_access_type=offline` is what asks for the refresh token. Without it
 * Dropbox issues a four-hour access token and nothing else.
 */
import type { OAuthApp } from '../oauth';
import type { ProviderMeta, StorageBackend } from './types';
import { CLIENT_IDS } from '../providerConfig';

const APP: OAuthApp = {
  clientId: CLIENT_IDS.dropbox,
  authorizeUrl: 'https://www.dropbox.com/oauth2/authorize',
  tokenUrl: 'https://api.dropboxapi.com/oauth2/token',
  // `files.content.*` is the narrowest pair that can write and read our own
  // files. Dropbox scopes an app to its own folder anyway when configured that
  // way, so this never sees the rest of someone's Dropbox.
  scopes: ['files.content.write', 'files.content.read'],
  extraAuthParams: { token_access_type: 'offline' },
};

export const dropboxMeta: ProviderMeta = {
  id: 'dropbox',
  name: 'Dropbox',
  blurb: 'Sign in once. The connection does not expire.',
  free: '2 GB free',
  auth: 'oauth',
  durability: {
    lasts: 'indefinite',
    note: 'Refresh tokens do not expire unless you revoke them.',
  },
  app: APP,
  needsSetup: !CLIENT_IDS.dropbox,
};

export function dropboxApp(): OAuthApp {
  return APP;
}

const ROOT = '/aliveguardian';

/** Dropbox rejects paths with a trailing slash or a double slash. */
function fullPath(path: string): string {
  return `${ROOT}/${path}`.replace(/\/+/g, '/');
}

export function dropboxBackend(accessToken: string): StorageBackend {
  const auth = { Authorization: `Bearer ${accessToken}` };

  return {
    async put(path, body) {
      // Dropbox takes the arguments in a header and the bytes as the raw body,
      // which is why this is not a JSON request.
      const res = await fetch('https://content.dropboxapi.com/2/files/upload', {
        method: 'POST',
        headers: {
          ...auth,
          'Content-Type': 'application/octet-stream',
          'Dropbox-API-Arg': JSON.stringify({
            path: fullPath(path),
            mode: 'overwrite',
            mute: true,
          }),
        },
        body: body as BodyInit,
      });
      if (!res.ok) throw new Error(await describe(res));
      const meta = (await res.json()) as { size?: number };
      return { size: meta.size ?? body.byteLength };
    },

    async get(path) {
      const res = await fetch('https://content.dropboxapi.com/2/files/download', {
        method: 'POST',
        headers: { ...auth, 'Dropbox-API-Arg': JSON.stringify({ path: fullPath(path) }) },
      });
      if (res.status === 409) return null; // Dropbox's "not found"
      if (!res.ok) throw new Error(await describe(res));
      return new Uint8Array(await res.arrayBuffer());
    },

    async remove(path) {
      const res = await fetch('https://api.dropboxapi.com/2/files/delete_v2', {
        method: 'POST',
        headers: { ...auth, 'Content-Type': 'application/json' },
        body: JSON.stringify({ path: fullPath(path) }),
      });
      if (res.status === 409) return; // already gone
      if (!res.ok) throw new Error(await describe(res));
    },
  };
}

/** Dropbox puts its real reason in a summary field; surface that, not "409". */
async function describe(res: Response): Promise<string> {
  const text = await res.text().catch(() => '');
  try {
    const j = JSON.parse(text) as { error_summary?: string };
    if (j.error_summary) return `Dropbox: ${j.error_summary.replace(/\/\.$/, '')}`;
  } catch {
    /* not JSON */
  }
  return `Dropbox returned ${res.status}${text ? ` — ${text.slice(0, 120)}` : ''}`;
}
